import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatRupees, formatRupeesCompact, INVESTMENT_TYPE_LABEL } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertCircle, Clock, MinusCircle, Grid3x3, LayoutList } from "lucide-react";
import FYPicker, { currentFYStart } from "@/components/FYPicker";

const STATUS = {
  received: { icon: CheckCircle2, color: "text-green-700", bg: "bg-green-50", border: "border-green-200", label: "Received" },
  late: { icon: AlertCircle, color: "text-red-700", bg: "bg-red-50", border: "border-red-200", label: "Overdue" },
  pending: { icon: Clock, color: "text-yellow-700", bg: "bg-yellow-50", border: "border-yellow-200", label: "Due this month" },
  no_income: { icon: MinusCircle, color: "text-slate-400", bg: "bg-slate-50", border: "border-slate-200", label: "No monthly income" },
};

const GRID_STATUS = {
  received: "bg-green-50 text-green-800 border-green-200",
  due: "bg-yellow-50 text-yellow-800 border-yellow-200",
  late: "bg-red-50 text-red-700 border-red-200",
  future: "bg-white text-slate-300 border-slate-100",
  empty: "bg-white text-slate-300 border-slate-100",
};

const monthLabel = (k) => {
  const [y, m] = k.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleString("en-IN", { month: "long", year: "numeric" });
};

/* --------- This month card view (default) --------- */
function ThisMonthView({ canEdit }) {
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [monthOffset, setMonthOffset] = useState(0); // 0 = current, -1 = last month, etc.

  const monthKey = (() => {
    const d = new Date();
    d.setMonth(d.getMonth() + monthOffset);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  })();

  const load = () => api.get(`/this-month?month_key=${monthKey}`).then((r) => setData(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [monthKey]);

  const save = async () => {
    setBusy(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      await api.post(`/investments/${edit.id}/transactions`, {
        investment_id: edit.id, date: today, direction: "in",
        amount: Number(amount),
        category: edit.type === "loan" ? "interest" :
                  edit.type === "sip" ? "principal" :
                  ["business_franchise", "company_share", "sebi_startup_fund", "lumpsum"].includes(edit.type) ? "profit_share" :
                  edit.type === "land" ? "rent" : "other",
        note: "Recorded from This-Month view",
      });
      toast.success("Recorded");
      setEdit(null); setAmount("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="text-slate-500 text-sm">Loading…</div>;

  return (
    <>
      {/* Quick month chips */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="month-chips">
        {[-3, -2, -1, 0].map((off) => {
          const d = new Date();
          d.setMonth(d.getMonth() + off);
          const label = off === 0 ? "This month" :
                        d.toLocaleString("en-IN", { month: "short", year: "2-digit" });
          return (
            <button key={off}
              onClick={() => setMonthOffset(off)}
              data-testid={`month-chip-${off}`}
              className={`text-[11px] font-medium px-3 py-1.5 rounded-full border transition-colors ${
                monthOffset === off ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* Hero card */}
      <div className="border border-slate-200 bg-white rounded-md p-6 sm:p-8">
        <div className="eyebrow">{monthLabel(data.month_key)}</div>
        <h2 className="mt-1 font-editorial font-light text-3xl sm:text-4xl md:text-5xl text-slate-900 tracking-[-0.03em]">
          <em className="italic">Rs {formatRupeesCompact(data.total_received).replace("Rs ", "")}</em>
          <span className="text-slate-400"> / {formatRupeesCompact(data.total_expected)}</span>
        </h2>
        <p className="mt-2 text-sm text-slate-500">
          Received this month · expected {formatRupees(data.total_expected)}
          {data.total_pending > 0 && (
            <span className="text-red-600"> · {formatRupees(data.total_pending)} pending</span>
          )}
        </p>
        {/* Progress bar */}
        <div className="mt-5 h-2 w-full bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-red-500 to-red-600 transition-all"
            style={{ width: `${Math.min(100, data.total_expected > 0 ? (data.total_received / data.total_expected) * 100 : 100)}%` }}
          />
        </div>
      </div>

      {/* Per-investment list — mobile-first */}
      <div>
        <div className="flex items-center justify-between mt-8 mb-3">
          <div className="eyebrow">By investment</div>
          <div className="text-[10px] text-slate-400 hidden sm:block">Tap any row to record</div>
        </div>
        <div className="border border-slate-200 bg-white rounded-md divide-y divide-slate-100 overflow-hidden">
          {data.cards.map((c) => {
            const s = STATUS[c.status];
            const pct = c.expected > 0 ? Math.min(100, (c.received / c.expected) * 100) : c.received > 0 ? 100 : 0;
            const canRecord = canEdit && c.status !== "no_income" && c.status !== "received";
            return (
              <button
                key={c.id}
                onClick={() => canRecord && (setEdit(c), setAmount(c.expected || ""))}
                disabled={!canRecord}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  canRecord ? "hover:bg-slate-50 cursor-pointer" : "cursor-default"
                }`}
                data-testid={`mi-row-${c.id}`}
              >
                {/* Status indicator */}
                <div className={`h-9 w-9 rounded-full border-2 ${s.border} ${s.bg} flex items-center justify-center shrink-0`}>
                  <s.icon className={`h-4 w-4 ${s.color}`} strokeWidth={2} />
                </div>

                {/* Name + type + progress */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <div className="text-[13.5px] font-semibold text-slate-900 truncate">{c.name}</div>
                    <div className="text-[9px] tracking-[0.16em] uppercase text-slate-400 whitespace-nowrap hidden sm:block">
                      {INVESTMENT_TYPE_LABEL[c.type]}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <div className={`text-[10px] font-medium ${s.color} shrink-0`}>{s.label}</div>
                    {c.expected > 0 && (
                      <div className="flex-1 min-w-0 max-w-[220px]">
                        <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full ${
                            c.status === "received" ? "bg-green-600" :
                            c.status === "late" ? "bg-red-500" :
                            c.status === "partial" ? "bg-amber-500" : "bg-slate-300"
                          }`} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Amount */}
                <div className="text-right shrink-0">
                  {c.expected > 0 ? (
                    <>
                      <div className="font-mono-fin text-[13px] text-slate-900">{formatRupeesCompact(c.received)}</div>
                      <div className="font-mono-fin text-[10px] text-slate-400">of {formatRupeesCompact(c.expected)}</div>
                    </>
                  ) : c.received > 0 ? (
                    <div className="font-mono-fin text-[13px] text-slate-900">{formatRupeesCompact(c.received)}</div>
                  ) : (
                    <div className="text-[10px] text-slate-300">—</div>
                  )}
                </div>

                {/* Chevron for record action */}
                {canRecord && (
                  <div className="text-red-600 shrink-0" aria-hidden>
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </div>
                )}
              </button>
            );
          })}
          {data.cards.length === 0 && (
            <div className="p-10 text-center text-slate-400 text-[13px]">No investments to show.</div>
          )}
        </div>
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record payment — {edit?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {edit?.expected > 0 && (
              <div className="text-sm text-slate-600">
                Expected: <span className="font-mono-fin font-medium text-slate-900">{formatRupees(edit.expected)}</span>
              </div>
            )}
            <div>
              <Label>Amount received (Rs)</Label>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus className="mt-2" data-testid="record-amount" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)} className="rounded-md">Cancel</Button>
            <Button onClick={save} disabled={busy || !amount} className="rounded-md bg-red-600 hover:bg-red-700 text-white" data-testid="record-save">
              {busy ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* --------- FY grid view (power-user) --------- */
function GridView({ canEdit }) {
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [fy, setFy] = useState(currentFYStart());

  const load = () => {
    const q = fy === "all" ? "" : `?fy=${fy}`;
    return api.get(`/monthly-income${q}`).then((r) => setData(r.data));
  };
  useEffect(() => { load(); }, [fy]);   // eslint-disable-line

  const save = async () => {
    setBusy(true);
    try {
      const [y, m] = edit.cell.month_key.split("-").map(Number);
      await api.post(`/investments/${edit.row.investment_id}/transactions`, {
        investment_id: edit.row.investment_id,
        date: `${y}-${String(m).padStart(2, "0")}-15`,
        direction: "in", amount: Number(amount),
        category: edit.row.type === "loan" ? "interest" :
                  ["business_franchise", "company_share"].includes(edit.row.type) ? "profit_share" : "other",
        note: "Manual entry via FY grid",
      });
      toast.success("Recorded");
      setEdit(null); setAmount("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="text-slate-500 text-sm">Loading grid…</div>;

  return (
    <>
      <p className="text-sm text-slate-500 mb-4">
        Full financial-year view. Green = received, Yellow = due, Red = late. Tap a cell to record.
      </p>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[11px] tracking-wider uppercase text-slate-400 font-medium">Fiscal year</span>
        <FYPicker value={fy} onChange={setFy} size="sm" />
      </div>
      <div className="border border-slate-200 rounded-md overflow-x-auto thin-scroll bg-white" data-testid="monthly-grid">
        <table className="w-full text-sm border-collapse min-w-[900px]">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="sticky left-0 z-10 bg-slate-50 p-3 text-left text-[10px] tracking-wider uppercase text-slate-500 min-w-[200px] border-r border-slate-200">Investment</th>
              {data.months.map((m) => (
                <th key={m.key} className="p-2 text-center text-[10px] tracking-wider uppercase text-slate-500 min-w-[64px]">{m.label}</th>
              ))}
              <th className="p-2 text-right text-[10px] tracking-wider uppercase text-slate-500 bg-slate-100 min-w-[90px]">Total</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => (
              <tr key={row.investment_id} className="border-b border-slate-100">
                <td className="sticky left-0 z-10 bg-white p-3 border-r border-slate-200">
                  <div className="font-medium text-slate-900">{row.name}</div>
                  <div className="text-xs text-slate-500">
                    {INVESTMENT_TYPE_LABEL[row.type]}
                    {row.expected_monthly > 0 && (
                      <span className="ml-2 font-mono-fin">exp {formatRupeesCompact(row.expected_monthly)}</span>
                    )}
                  </div>
                </td>
                {row.cells.map((c) => (
                  <td key={c.month_key} className="p-1 text-center align-middle">
                    <button
                      onClick={() => canEdit && setEdit({ row, cell: c }) || setAmount(c.amount || "")}
                      disabled={!canEdit}
                      data-testid={`cell-${row.investment_id}-${c.month_key}`}
                      className={`w-full h-12 border ${GRID_STATUS[c.status]} px-1 text-[11px] font-mono-fin ${canEdit ? "cursor-pointer hover:brightness-95" : "cursor-default"}`}
                    >
                      {c.amount > 0
                        ? formatRupeesCompact(c.amount).replace("Rs ", "")
                        : c.status === "late" ? "!" : c.status === "due" ? "·" : ""}
                    </button>
                  </td>
                ))}
                <td className="p-2 text-right bg-slate-50 font-mono-fin font-semibold text-slate-900">
                  {formatRupeesCompact(row.total)}
                </td>
              </tr>
            ))}
            <tr className="bg-slate-900 text-white">
              <td className="sticky left-0 z-10 bg-slate-900 p-3 text-[11px] tracking-wider uppercase">Total</td>
              {data.totals_per_month.map((tot) => (
                <td key={tot.key} className="p-2 text-center text-[11px] font-mono-fin">
                  {tot.total > 0 ? formatRupeesCompact(tot.total).replace("Rs ", "") : "-"}
                </td>
              ))}
              <td className="p-2 text-right font-mono-fin font-bold" data-testid="grand-total">
                {formatRupeesCompact(data.grand_total)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record — {edit?.row.name} · {edit?.cell.label} {edit?.cell.year}</DialogTitle>
          </DialogHeader>
          <div>
            <Label>Amount (Rs)</Label>
            <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus className="mt-2" data-testid="grid-amount" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)} className="rounded-md">Cancel</Button>
            <Button onClick={save} disabled={busy || !amount} className="rounded-md bg-red-600 hover:bg-red-700 text-white" data-testid="grid-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function MonthlyIncome() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "accounts";
  const [tab, setTab] = useState("this-month");

  return (
    <div className="space-y-6">
      <div>
        <div className="eyebrow">Income</div>
        <h1 className="mt-2 font-editorial font-light text-[38px] leading-[1.02] sm:text-5xl md:text-6xl text-slate-900 tracking-[-0.035em]" data-testid="monthly-title">
          This <em className="italic">month.</em>
        </h1>
        <p className="mt-2 text-sm text-slate-500 max-w-md">
          A calm view of what&apos;s come in this month, and what&apos;s still owed.
        </p>
      </div>

      <div className="inline-flex items-center bg-slate-100 rounded-lg p-1" data-testid="income-tabs">
        <button
          onClick={() => setTab("this-month")}
          className={`flex items-center gap-2 px-3 py-1.5 text-[12px] font-medium rounded-md transition-colors ${tab === "this-month" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}
          data-testid="tab-this-month"
        >
          <LayoutList className="h-3.5 w-3.5" strokeWidth={1.75} /> This month
        </button>
        <button
          onClick={() => setTab("grid")}
          className={`flex items-center gap-2 px-3 py-1.5 text-[12px] font-medium rounded-md transition-colors ${tab === "grid" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}
          data-testid="tab-grid"
        >
          <Grid3x3 className="h-3.5 w-3.5" strokeWidth={1.75} /> Full year
        </button>
      </div>

      {tab === "this-month" ? <ThisMonthView canEdit={canEdit} /> : <GridView canEdit={canEdit} />}
    </div>
  );
}
